import { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useQuery, useAction } from "wasp/client/operations";
import { useAuth } from "wasp/client/auth";
import { getInvitationByToken, acceptInvitationByToken, declineInvitationByToken } from "wasp/client/operations";
import { Button } from "../client/components/ui/button";
import { Card, CardContent, CardHeader } from "../client/components/ui/card";
import { CheckCircle, XCircle, Eye } from "lucide-react";
import useTranslation from "../client/hooks/useTranslation";
import { useToast } from "../client/hooks/use-toast";

export default function AcceptInvitationPage() {
  const t = useTranslation();
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { data: user } = useAuth();
  const [status, setStatus] = useState<"idle" | "accepting" | "declining" | "done_accepted" | "done_declined">("idle");

  const { data: rawInvitation, isLoading, error } = useQuery(
    getInvitationByToken,
    { token: token ?? "" },
    { enabled: !!token },
  );

  const invitation = rawInvitation as {
    id: string;
    status: string;
    expiresAt: Date;
    inviteeEmail: string;
    mentorFirstName: string | null;
    mentorLastName: string | null;
    mentorEmail: string | null;
    token: string;
  } | null | undefined;

  const acceptFn = useAction(acceptInvitationByToken);
  const declineFn = useAction(declineInvitationByToken);

  useEffect(() => {
    if (token) {
      sessionStorage.setItem("pendingInviteToken", token);
    }
  }, [token]);

  const mentorName =
    [invitation?.mentorFirstName, invitation?.mentorLastName].filter(Boolean).join(" ") || "Un tuteur";

  const isExpiredOrInvalid =
    !isLoading &&
    status !== "done_accepted" &&
    status !== "done_declined" &&
    (!!error ||
      !invitation ||
      invitation.status !== "pending" ||
      new Date(invitation.expiresAt) < new Date());

  const handleAccept = async () => {
    if (!token) return;
    setStatus("accepting");
    try {
      await acceptFn({ token });
      setStatus("done_accepted");
      sessionStorage.removeItem("pendingInviteToken");
      setTimeout(() => navigate("/account"), 2000);
    } catch (err: unknown) {
      toast({
        title: "Erreur",
        description: err instanceof Error ? err.message : "Une erreur est survenue",
        variant: "destructive",
      });
      setStatus("idle");
    }
  };

  const handleDecline = async () => {
    if (!token) return;
    setStatus("declining");
    try {
      await declineFn({ token });
      setStatus("done_declined");
    } catch (err: unknown) {
      toast({
        title: "Erreur",
        description: err instanceof Error ? err.message : "Une erreur est survenue",
        variant: "destructive",
      });
      setStatus("idle");
    }
  };

  const loginPath = `/login?next=${encodeURIComponent(typeof window !== "undefined" ? window.location.pathname : "")}`;

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <Card className="w-full max-w-md bg-card border-border">
        <CardHeader className="pb-2 pt-6 px-6">
          <h1 className="text-xl font-bold text-foreground">
            edgely<span className="text-primary">.</span>
          </h1>
        </CardHeader>
        <CardContent className="px-6 pb-8 pt-4">
          {isLoading ? (
            <p className="text-sm text-muted-foreground">Chargement de l'invitation...</p>
          ) : isExpiredOrInvalid ? (
            <div className="space-y-3">
              <div className="flex items-center gap-2 text-pnlNegative">
                <XCircle className="h-5 w-5 shrink-0" />
                <p className="text-sm font-medium">{t.mentor.invitationExpired}</p>
              </div>
              <Link to="/login">
                <Button variant="outline" size="sm" className="mt-2">
                  Retour à l'accueil
                </Button>
              </Link>
            </div>
          ) : invitation ? (
            <div>
              {status === "done_accepted" && (
                <div className="flex items-center gap-2 text-primary">
                  <CheckCircle className="h-5 w-5 shrink-0" />
                  <p className="text-sm font-medium">Invitation acceptée ! Redirection...</p>
                </div>
              )}

              {status === "done_declined" && (
                <div className="space-y-3">
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <XCircle className="h-5 w-5 shrink-0" />
                    <p className="text-sm">{t.mentor.invitationDeclinedConfirm}</p>
                  </div>
                  <Link to="/dashboard">
                    <Button variant="outline" size="sm">Aller au dashboard</Button>
                  </Link>
                </div>
              )}

              {(status === "idle" || status === "accepting" || status === "declining") && (
                <div className="space-y-5">
                  <div className="space-y-1">
                    <p className="text-xs text-muted-foreground">{t.mentor.invitationFrom}</p>
                    {invitation?.mentorEmail && (
                      <p className="text-base font-semibold text-foreground">{invitation.mentorEmail}</p>
                    )}
                  </div>

                  <div className="space-y-2">
                    <p className="text-xs text-muted-foreground">{t.mentor.grantedPermissions}</p>
                    <ul className="space-y-1">
                      {[
                        t.mentor.canViewJournal,
                        t.mentor.canViewDashboard,
                        t.mentor.canViewAnalytics,
                        t.mentor.canViewSessions,
                        t.mentor.canViewPropFirmManager,
                      ].map((perm) => (
                        <li key={perm} className="flex items-center gap-2 text-sm text-foreground/80">
                          <Eye className="h-3.5 w-3.5 text-primary shrink-0" />
                          {perm}
                        </li>
                      ))}
                    </ul>
                    <p className="text-xs text-muted-foreground mt-1">
                      Vous pourrez ajuster ces accès à tout moment depuis votre compte.
                    </p>
                  </div>

                  {!user ? (
                    <div className="space-y-3 pt-2">
                      <p className="text-sm text-muted-foreground">{t.mentor.loginToAccept}</p>
                      <Link to={loginPath}>
                        <Button className="w-full bg-primary hover:bg-primary/90 text-primary-foreground font-semibold">
                          Se connecter
                        </Button>
                      </Link>
                      <Link to="/signup">
                        <Button variant="outline" className="w-full">
                          Créer un compte
                        </Button>
                      </Link>
                    </div>
                  ) : (
                    <div className="flex gap-3 pt-2">
                      <Button
                        className="flex-1 bg-primary hover:bg-primary/90 text-primary-foreground font-semibold"
                        onClick={handleAccept}
                        disabled={status !== "idle"}
                      >
                        {status === "accepting" ? "Acceptation..." : t.mentor.acceptInvitation}
                      </Button>
                      <Button
                        variant="outline"
                        className="flex-1"
                        onClick={handleDecline}
                        disabled={status !== "idle"}
                      >
                        {status === "declining" ? "..." : t.mentor.declineInvitation}
                      </Button>
                    </div>
                  )}
                </div>
              )}
            </div>
          ) : null}
        </CardContent>
      </Card>
    </div>
  );
}
